import { isDocxFile } from './consent_upload_file.helpers';
/** Above consent_converter's 300s budget and below its 360s kill, so its 504 reaches the author. */
export const CONSENT_CONVERSION_TIMEOUT_MS = 345 * 1000;
export const CONSENT_CONVERSION_FAILED = 'CONSENT_CONVERSION_FAILED';
export const CONSENT_DOCX_CONVERSION_NOTICE = 'Word files are converted to PDF before they are saved.';
/** Must match consent_converter `metadata.reason` codes the author can act on. */
const CONVERSION_FAILURE_MESSAGES = {
    too_large: 'The Word document is larger than 25 MB.',
    not_docx: 'The file is not a Word (.docx) document.',
    unzip_too_large: 'The Word document is too large once unpacked.',
    too_many_entries: 'The Word document has too many embedded parts.',
    too_many_pages: 'The converted PDF has more than 300 pages.',
    pdf_too_large: 'The converted PDF is larger than 30 MB.',
    timeout: 'Conversion took longer than 5 minutes.',
};
const GENERIC_CONVERSION_FAILURE_MESSAGE = 'The Word document could not be converted.';
export class ConsentConversionError extends Error {
    constructor(reason) {
        super(reason ? CONVERSION_FAILURE_MESSAGES[reason] : GENERIC_CONVERSION_FAILURE_MESSAGE);
        this.code = CONSENT_CONVERSION_FAILED;
        this.name = 'ConsentConversionError';
        this.reason = reason;
    }
}
/** Untranslated English sentence naming why the convert failed; pass it through `translate`. */
export const consentConversionFailureMessage = (error) => error.reason ? CONVERSION_FAILURE_MESSAGES[error.reason] : GENERIC_CONVERSION_FAILURE_MESSAGE;
const isConsentConversionReason = (value) => typeof value === 'string' && Object.prototype.hasOwnProperty.call(CONVERSION_FAILURE_MESSAGES, value);
const isArrayBufferLike = (data) => ArrayBuffer.isView(data) || Object.prototype.toString.call(data) === '[object ArrayBuffer]';
const decodeErrorBody = (data) => {
    if (isArrayBufferLike(data)) {
        try {
            return JSON.parse(new TextDecoder().decode(data));
        }
        catch {
            return undefined;
        }
    }
    return data;
};
/** The request uses `responseType: 'arraybuffer'`, so a JSON error body arrives as bytes. */
const conversionReasonFrom = (error) => {
    if (!error || typeof error !== 'object') {
        return undefined;
    }
    const { code, response } = error;
    if (code === 'ECONNABORTED' || code === 'ETIMEDOUT') {
        return 'timeout';
    }
    const body = decodeErrorBody(response?.data);
    const reason = body?.metadata?.reason;
    return isConsentConversionReason(reason) ? reason : undefined;
};
export const isConsentConversionError = (error) => typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    error.code === CONSENT_CONVERSION_FAILED;
const startsWithPdfMagic = (bytes) => bytes.length >= 4 && bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46;
const asBytes = (data) => {
    if (data instanceof Uint8Array) {
        return data;
    }
    if (data instanceof ArrayBuffer) {
        return new Uint8Array(data);
    }
    if (typeof Buffer !== 'undefined' && Buffer.isBuffer(data)) {
        return new Uint8Array(data);
    }
    return new Uint8Array(0);
};
/**
 * Template and asset names are stored as PDF after a DocX convert. A Word file recognised only by
 * MIME keeps its name and gains `.pdf`; a PDF keeps its original name (U-NAME-02).
 */
export const consentStoredFileName = (file) => isDocxFile(file) ? `${file.name.replace(/\.docx$/i, '')}.pdf` : file.name;
/**
 * SSP-3587: ask consent_converter for a PDF render. The original DocX is not retained.
 */
export const convertConsentDocxToPdf = async (postCloud, firebaseToken, file) => {
    if (!isDocxFile(file)) {
        return file;
    }
    if (!firebaseToken) {
        throw new Error('Firebase auth session is unavailable. Reload the page and log in again.');
    }
    try {
        const form = new FormData();
        form.append('file', file, file.name);
        const { data } = await postCloud('/consent_converter/v1/convert', form, {
            headers: { 'Authorization': `Bearer ${firebaseToken}`, 'Content-Type': 'multipart/form-data' },
            responseType: 'arraybuffer',
            timeout: CONSENT_CONVERSION_TIMEOUT_MS,
            no400Handling: true,
            no500Handling: true,
        });
        const bytes = asBytes(data);
        if (!startsWithPdfMagic(bytes)) {
            throw new ConsentConversionError();
        }
        return new File([bytes], consentStoredFileName(file), { type: 'application/pdf' });
    }
    catch (error) {
        if (error instanceof ConsentConversionError) {
            throw error;
        }
        throw new ConsentConversionError(conversionReasonFrom(error));
    }
};
